import { Globals } from '../../modules/Globals';
import axios from 'axios';
import { IItem } from '../../interfaces/IItem';
import { ICategory } from '../../interfaces/ICategory';
import { IOption } from '../../interfaces/IOption';
import { IItemOption } from '../../interfaces/IItemOption';
import { IItemResumed } from '../../interfaces/IItemResumed';
import { ISimpleHourRange } from '../../interfaces/ISimpleHourRange';
import { IItemSearchResumed } from '../../interfaces/IItemSearchResumed';
import { IItemPriceChange } from '../../interfaces/PriceChanges';
import IItemImport from '../../interfaces/IItemImport';

const apiUrl = Globals.apiPath + '/items';
const v2ApiUrl = Globals.apiPath + '/v2/items';

/** Links-only payload for PUT /items/:id/options (option content is saved via /options). */
export interface IItemOptionLink {
  optionId: number;
  active?: boolean;
  index?: number;
  deleted?: boolean;
}

export default class ItemsService {
  constructor() {}

  static async getAll(
    restaurantId: number,
    categoryId?: number,
    menuId: number = null,
    where?: Partial<Record<keyof IItem, any>>,
    withOptions: boolean = false
  ): Promise<IItem[]> {
    let res = await axios.get<IItem[]>(
      `${apiUrl}?r=${restaurantId}` +
        (categoryId ? `&c=${categoryId}` : '') +
        (menuId ? `&m=${menuId}` : '') +
        (where ? `&w=${encodeURIComponent(JSON.stringify(where))}` : '') +
        (withOptions ? `&o=1` : '')
    );
    return res.data;
  }

  static async getAllResumed({
    restaurantId,
    categoryIds,
    menuIds,
    where,
  }: {
    restaurantId: number;
    categoryIds?: number[];
    menuIds?: number[];
    where?: Partial<Record<keyof IItem, any>>;
  }): Promise<IItemResumed[]> {
    let res = await axios.get<IItemResumed[]>(
      `${apiUrl}/resumed?r=${restaurantId}` +
        (menuIds?.length ? `&m=${menuIds.join(',')}` : '') +
        (categoryIds?.length ? `&c=${categoryIds.join(',')}` : '') +
        (where ? `&w=${encodeURIComponent(JSON.stringify(where))}` : '')
    );
    return res.data;
  }

  static async getAllFull({
    restaurantId,
    categoryId,
    menuId = null,
    where,
  }: {
    restaurantId: number;
    categoryId?: number | string;
    menuId?: number | string;
    where?: Partial<Record<keyof IItem, any>>;
  }): Promise<IItem[]> {
    let res = await axios.get<IItem[]>(
      `${apiUrl}/full?r=${restaurantId}` +
        (categoryId ? `&c=${categoryId}` : '') +
        (menuId ? `&m=${menuId}` : '') +
        (where ? `&w=${encodeURIComponent(JSON.stringify(where))}` : '')
    );
    return res.data;
  }

  async getAllByName(name: string): Promise<IItem[]> {
    let res = await axios.get<IItem[]>(
      `${apiUrl}/name/${encodeURIComponent(name)}`
    );
    return res.data;
  }

  async searchResumed(
    restaurantId: number,
    branchId: number,
    search: string = '',
    menuId: number | null | 'all'
  ): Promise<IItemSearchResumed[]> {
    let res = await axios.get<IItemSearchResumed[]>(
      `${apiUrl}/search/${encodeURIComponent(
        search ?? ''
      )}?r=${restaurantId}&b=${branchId}` + (menuId ? `&m=${menuId}` : '')
    );
    return res.data;
  }

  async getCount(restaurantId: number) {
    let res = await axios.get<any>(`${apiUrl}/count?r=${restaurantId}`);
    return res.data.count as number;
  }

  async getById(id: number): Promise<IItem> {
    let res = await axios.get<IItem>(`${apiUrl}/${id}`);
    return res.data;
  }

  async getOptions(id: number) {
    let res = await axios.get<IItemOption[]>(`${apiUrl}/${id}/options`);
    return res.data;
  }

  static async getFinalOptions(id: number) {
    let res = await axios.get<IOption[]>(`${apiUrl}/${id}/options?final=true`);
    return res.data;
  }

  async create(item: IItem): Promise<IItem> {
    let res = await axios.post<IItem>(apiUrl, item);
    return res.data;
  }

  async saveOrder(restaurantId: number, ids: number[], indexes: number[]) {
    let res = await axios.put(`${apiUrl}/saveOrder`, {
      restaurantId,
      ids,
      indexes,
    });
    return res.data;
  }

  static async massUpdateLabelTemplate(
    restaurantId: number,
    ids: number[],
    labelTemplate: string
  ) {
    let res = await axios.put(`${apiUrl}/labelTemplate`, {
      restaurantId,
      ids,
      labelTemplate,
    });

    return res.data;
  }

  async saveHours(itemId: number, hours: ISimpleHourRange[]) {
    let res = await axios.put(`${apiUrl}/${itemId}/hours`, { hours });
    return res.data;
  }

  async massPriceChanges(
    changes: IItemPriceChange[]
  ): Promise<IItemPriceChange[]> {
    let res = await axios.put<IItemPriceChange[]>(
      `${apiUrl}/massPriceChange`,
      changes
    );
    return res.data;
  }

  async undoPriceChanges(
    changes: IItemPriceChange[]
  ): Promise<IItemPriceChange[]> {
    let res = await axios.put<IItemPriceChange[]>(
      `${apiUrl}/undoPriceChange`,
      changes
    );
    return res.data;
  }

  async update(item: IItem): Promise<IItem> {
    let res = await axios.put<IItem>(`${apiUrl}/${item.id}`, item);
    return res.data;
  }

  async saveOptions(
    item: IItem,
    options: IItemOptionLink[]
  ): Promise<IItem> {
    let res = await axios.put<IItem>(`${apiUrl}/${item.id}/options`, {
      options,
    });
    return res.data;
  }

  async importBeta2(
    restaurantId: number,
    items: IItemImport[],
    menuId?: number,
    overKey?: string
  ): Promise<IItem> {
    let res = await axios.post<IItem>(`${apiUrl}/importBeta2`, {
      restaurantId,
      items,
      menuId,
      overKey,
    });
    return res.data;
  }

  async delete(item: IItem): Promise<IItem> {
    let res = await axios.delete<IItem>(`${apiUrl}/${item.id}`);
    return res.data;
  }

  async activate(item: IItem): Promise<IItem> {
    let res = await axios.post<IItem>(`${apiUrl}/${item.id}/activate`);
    return res.data;
  }

  async deactivate(item: IItem): Promise<IItem> {
    let res = await axios.post<IItem>(`${apiUrl}/${item.id}/deactivate`);
    return res.data;
  }

  // V2

  /** Every live product with sizes and links; `ids` (at most 200) reads only those. */
  static async getAllV2({
    restaurantId,
    ids,
  }: {
    restaurantId: number;
    ids?: number[];
  }): Promise<IItem[]> {
    let res = await axios.get<IItem[]>(`${v2ApiUrl}/full/r/${restaurantId}`, {
      params: ids ? { ids: ids.join(',') } : undefined,
    });
    return res.data;
  }
}
